import type { Permission } from '@/shared/api/resources';

/**
 * Grupos de permissao na ordem do quadro de perfis do OnPoint.
 *
 * O OnPoint mostra, em cada perfil, os grupos "Permissoes - Kanban", "Permissoes -
 * Parametrizacao" e assim por diante, nessa ordem. O catalogo do SeguraCore e mais
 * fino (uma chave por modulo, recurso e acao), e por isso cada chave cai no grupo
 * do OnPoint que corresponde a area dela. O que nao tem grupo no OnPoint vai para
 * "Outras permissoes do SeguraCore", no fim: a tela mostra tudo o que existe, e nao
 * so o que o OnPoint tinha.
 *
 * Os grupos ja nascem com os modulos que outros pacotes vao criar (esteiras,
 * segurados, bi, parametrizacao): a chave nova entra no grupo certo sem mexer aqui.
 */
export interface GrupoDePermissao {
  titulo: string;
  /** Modulos inteiros do grupo. */
  modulos: string[];
  /** Prefixos de chave (modulo.recurso) que vencem o modulo. */
  recursos?: string[];
}

export const gruposDoOnPoint: GrupoDePermissao[] = [
  {
    titulo: 'Permissoes - Kanban',
    modulos: ['esteiras', 'demandas', 'subscricao'],
    // Os pipes de credito do OnPoint sao a decisao de credito.
    recursos: ['credito.analise'],
  },
  {
    titulo: 'Permissoes - Parametrizacao',
    modulos: [
      'parametrizacao',
      'produtos',
      'taxas',
      'agravos',
      'configuracoes',
      'templates',
      'seguradoras',
      'bancos',
      'catalogos',
    ],
    recursos: ['credito.parametro', 'credito.motor'],
  },
  {
    titulo: 'Permissoes - Propostas',
    modulos: [
      'operacoes',
      'comercial',
      'juridico',
      'minutas',
      'assinaturas',
      'emissao',
    ],
  },
  {
    titulo: 'Permissoes - Apolices',
    modulos: [
      'apolices',
      'endossos',
      'cancelamentos',
      'renovacoes',
      'fiancas',
      'sinistros',
    ],
  },
  { titulo: 'Permissoes - Corretores', modulos: ['corretores', 'comissoes'] },
  {
    titulo: 'Permissoes - Tomador',
    modulos: ['tomadores', 'ccg'],
    // "Habilitar visualizacao de analise de credito" e as consultas sao do tomador.
    // "Gerenciar documentos" do tomador (USU-076) e chave do modulo de documentos,
    // mas o OnPoint a mostra aqui.
    recursos: ['credito.consulta', 'credito.dado', 'documentos.tomador'],
  },
  { titulo: 'Permissoes - Segurado', modulos: ['segurados'] },
  { titulo: 'Permissoes - Usuario', modulos: ['usuarios'] },
  { titulo: 'Permissoes - Documentos', modulos: ['documentos'] },
  { titulo: 'Permissoes - BI', modulos: ['bi', 'dashboard'] },
  { titulo: 'Permissoes - Integracoes', modulos: ['integracoes'] },
  {
    titulo: 'Permissoes - Gerenciamento de Erro de Consulta',
    modulos: [],
    recursos: ['integracoes.erro_consulta', 'integracoes.filtro_mandatorio'],
  },
];

export const grupoDasOutras = 'Outras permissoes do SeguraCore';

/**
 * Chaves que mudam de grupo dentro do mesmo modulo: a leitura da analise de credito
 * fica no tomador, como no OnPoint, e a decisao fica no Kanban.
 */
const chavesAvulsas: Record<string, string> = {
  'credito.analise.view': 'Permissoes - Tomador',
};

/** Grupo do OnPoint de uma permissao do catalogo. */
export function grupoDaPermissao(
  permissao: Pick<Permission, 'key' | 'module'>,
): string {
  const avulsa = chavesAvulsas[permissao.key];
  if (avulsa) return avulsa;

  const [modulo = permissao.module, recurso = ''] = permissao.key.split('.');
  const prefixo = `${modulo}.${recurso}`;
  const porRecurso = gruposDoOnPoint.find((grupo) => grupo.recursos?.includes(prefixo));
  if (porRecurso) return porRecurso.titulo;

  const porModulo = gruposDoOnPoint.find((grupo) =>
    grupo.modulos.includes(permissao.module || modulo),
  );
  return porModulo ? porModulo.titulo : grupoDasOutras;
}

/**
 * Agrupa as permissoes na ordem do OnPoint, com as do SeguraCore sem grupo no fim.
 * Grupo sem nenhuma permissao fica de fora.
 */
export function agruparPermissoes<T extends Pick<Permission, 'key' | 'module'>>(
  permissoes: T[],
): { titulo: string; itens: T[] }[] {
  const porGrupo = new Map<string, T[]>();
  for (const permissao of permissoes) {
    const titulo = grupoDaPermissao(permissao);
    porGrupo.set(titulo, [...(porGrupo.get(titulo) ?? []), permissao]);
  }
  const ordem = [...gruposDoOnPoint.map((grupo) => grupo.titulo), grupoDasOutras];
  return ordem
    .filter((titulo) => (porGrupo.get(titulo)?.length ?? 0) > 0)
    .map((titulo) => ({ titulo, itens: porGrupo.get(titulo) ?? [] }));
}
